import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import type { AppProps /*, AppContext */ } from 'next/app';

import { FC, useReducer, useState, useEffect, ReactNode, ReactElement } from 'react';

import Head from 'next/head';
import type { NextPage } from 'next';
import { analytics } from '@modules/analytics';

import { layoutContext } from '@context/layout';

import { getOrCreateContext } from '@context/singleton-manager';
import { navBookmarkContext } from '@context/nav-bookmark-context';
import useSingletonContext from '@hooks/use-singleton-context';
import { ProgressChecklistContext } from '@context/progress-checklist-context';

import { TourContext } from '@context/tour-context';
import useTour from '@hooks/use-tour';

import type { NavigationState, NavigationAction } from 'types';
import { TABLET_LOWER_THRESHOLD, DESKTOP_LOWER_THRESHOLD } from 'site-constants';

import '@styles/globals.scss';
import '@reach/skip-nav/styles.css';
import '@reach/dialog/styles.css';
import 'react-image-crop/dist/ReactCrop.css';
import { useRouter } from 'next/router';
import useProgressChecklist from '@hooks/use-progress-checklist';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from '@services/apollo';
import NotificationsProvider from '@components/Notifications/NotificationsProvider';
import { AnalyticsPagePropertiesInput, AnalyticsPlatforms } from '.cache/__types__';
import { logger } from '@modules/logger';
import { UserDetailsProvider } from '@modules/user-details/UserDetails';
import { HighlightPopupService } from '@components/SideNav/HighlightPopup/HighlighPopupService';
import { AmplitudeExperimentsProvider } from '@modules/feature-flag';
import { GlobalModalProvider } from '@modules/global-modals';

import { MantineProvider } from '@mantine/core';
import { theme } from 'ui-lib/mantine/theme';
import { Notifications } from '@mantine/notifications';
import { MonitoringProvider } from '@modules/monitoring/MonitoringProvider';
import { useCookieConsentListener } from '@hooks/use-cookie-consent-listener';

interface CustomPageProps {
  isAuthenticated: boolean;
  [key: string]: unknown; // Allow additional properties if needed
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type NextPageWithLayout<T = void> = NextPage<CustomPageProps & T> & {
  getLayout?: (page: ReactElement<T>) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout<unknown>;
  pageProps: CustomPageProps;
};

const hoverPopoverContext = getOrCreateContext('hover');
const overlayContext = getOrCreateContext('overlay');
const navigationContext = getOrCreateContext('navigation');

function useNavigationReducer(): [NavigationState, (action: NavigationAction) => void] {
  const [state, dispatch] = useReducer(
    (state: NavigationState, action: NavigationAction) => {
      switch (action.type) {
        case 'MINIMIZE':
          return {
            ...state,
            minimized: true,
          };
        case 'APPEAR':
          return {
            ...state,
            minimized: false,
          };
        case 'COLLAPSE':
          return {
            collapsed: true,
            minimized: true,
            overridden: action?.override || state.overridden,
          };
        case 'EXPAND':
          return {
            collapsed: false,
            minimized: true,
            overridden: action?.override || state.overridden,
          };
        case 'TOGGLE_COLLAPSE':
          return {
            ...state,
            collapsed: !state.collapsed,
            overridden: action?.override || state.overridden,
          };
        default:
          return state;
      }
    },
    { collapsed: undefined, minimized: undefined, overridden: false }
  );

  return [state, dispatch];
}

/**
 * Ensures that the nav state is in sync with SSR version after the first render. Because the nav is both
 * reactive to the screen size and can be changed by user input we need to model it in state. However to ensure
 * SSR doesn't cause the nav to jump around on first render we need to use media breakpoints in CSS to control how
 * it's rendered, we can't rely on Javascript. This means we need to reflect the nav state in react after the
 * component is mounted.
 */
function useSetupNavState(navState: NavigationState, navDispatch: (action: NavigationAction) => void) {
  useEffect(() => {
    if (navState.collapsed !== undefined && navState.minimized !== undefined) return;
    const windowWidth = window.innerWidth;

    if (windowWidth < TABLET_LOWER_THRESHOLD) navDispatch({ type: 'COLLAPSE' });
    else if (windowWidth >= TABLET_LOWER_THRESHOLD && windowWidth < DESKTOP_LOWER_THRESHOLD) {
      navDispatch({ type: 'COLLAPSE' });
    } else navDispatch({ type: 'EXPAND' });
  }, [navState.collapsed, navState.minimized, navDispatch]);
}

const platforms = ['android', 'api', 'card_worker', 'dash', 'firebase_functions', 'ios', 'webflow'];
function isPlatform(candidate: unknown): candidate is AnalyticsPlatforms {
  return typeof candidate === 'string' && platforms.includes(candidate);
}

const App: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  const [navState, navDispatch] = useNavigationReducer();

  const [popoverContextProps] = useSingletonContext();
  const [overlayContextProps] = useSingletonContext();
  const [navigationContextProps] = useSingletonContext();
  const [navBookmarks, setNavBookmarks] = useState({});
  const tourContextProps = useTour();
  const progressChecklistContextProps = useProgressChecklist();

  useSetupNavState(navState, navDispatch);
  const router = useRouter();

  useEffect(() => {
    const node = document.querySelector('.preload');
    node?.classList?.remove('preload');
  }, []);

  // page analytics tracker
  useEffect(() => {
    const trackPageWithPath = (path: string) => {
      try {
        const url = new URL(path, 'https://blnq.xyz'); // use a fake baseURL
        const properties: AnalyticsPagePropertiesInput = {
          path: url.pathname,
        };

        // grab the query string params we want
        url.searchParams.forEach((value, key) => {
          switch (key) {
            case 'utm_medium':
              properties[key] = value;
              break;
            case 'utm_source':
              properties[key] = value;
              break;
            case 'utm_campaign':
              properties[key] = value;
              break;
            case 'utm_content':
              properties[key] = value;
              break;
            case 'utm_term':
              properties[key] = value;
              break;
            case 'ref_original':
              properties[key] = value;
              break;
            case 'gclid':
              properties[key] = value;
              if (value) {
                localStorage.setItem('gclid', value); // save for when user is created
              }
              break;
            case 'fbclid':
              properties[key] = value;
              if (value) {
                localStorage.setItem('fbclid', value); // save for when user is created
              }
              break;
            case 'ref_page':
              properties[key] = value;
              break;
            case 'ref_path':
              properties[key] = value;
              break;
            case 'ref_cta':
              properties[key] = value;
              break;
            case 'ref_platform':
              if (isPlatform(value)) {
                properties[key] = value;
              }
              break;
          }
        });

        analytics.page({ properties });
      } catch (error) {
        logger.warn('Page tracking failed', { error });
      }
    };

    if (router.isReady) {
      trackPageWithPath(router.asPath); // track pages after the router is ready
    }
  }, [router.asPath, router.isReady]); // fire when the path or ready changes

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const settingsPageRegex = /^.*\/settings\//;
      const isCurrentlyOnSettingsPage = settingsPageRegex.test(router.asPath);
      const isMovingToSettingsPage = settingsPageRegex.test(url);

      if (!isCurrentlyOnSettingsPage && isMovingToSettingsPage) {
        setNavBookmarks((navBookmarks) => ({
          ...navBookmarks,
          settings: router.asPath,
        }));
      }
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, router.asPath]);

  useCookieConsentListener();

  const layout = Component.getLayout ?? ((page: ReactElement) => page);

  return (
    <>
      <ApolloProvider client={apolloClient}>
        <UserDetailsProvider isAuthenticated={pageProps.isAuthenticated}>
          <AmplitudeExperimentsProvider>
            <GlobalModalProvider>
              <layoutContext.Provider value={{ state: navState, dispatch: navDispatch }}>
                <Head>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                </Head>
                <NotificationsProvider>
                  <overlayContext.Provider value={overlayContextProps}>
                    <hoverPopoverContext.Provider value={popoverContextProps}>
                      <navigationContext.Provider value={navigationContextProps}>
                        <navBookmarkContext.Provider value={navBookmarks}>
                          <ProgressChecklistContext.Provider value={progressChecklistContextProps}>
                            <TourContext.Provider value={tourContextProps}>
                              <HighlightPopupService>
                                <MantineProvider theme={theme}>
                                  {layout(
                                    <>
                                      <Notifications />
                                      <Component {...pageProps} />
                                    </>
                                  )}
                                </MantineProvider>
                              </HighlightPopupService>
                            </TourContext.Provider>
                          </ProgressChecklistContext.Provider>
                        </navBookmarkContext.Provider>
                      </navigationContext.Provider>
                    </hoverPopoverContext.Provider>
                  </overlayContext.Provider>
                </NotificationsProvider>
              </layoutContext.Provider>
            </GlobalModalProvider>
          </AmplitudeExperimentsProvider>
        </UserDetailsProvider>
      </ApolloProvider>
      <MonitoringProvider />
    </>
  );
};

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext: AppContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);

//   return { ...appProps }
// }

export default App;
